<script setup>
import { ref, onMounted, defineProps, defineEmits, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappFlowsAPI from 'dashboard/api/inboxes/whatsappFlows';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
  // Survey type the flow belongs to: 'csat' or 'nps'.
  flowType: { type: String, default: 'csat' },
});

// The parent gates the survey toggle on whether a flow is actually published, so the
// status has to travel back up as it changes.
const emit = defineEmits(['statusChange']);

const { t } = useI18n();

const isLoading = ref(false);
const isCreating = ref(false);
const isDeleting = ref(false);
const deleteDialogRef = ref(null);
const flowStatus = ref({
  configured: false,
  flowId: null,
  enabled: true,
});

const isFlowConfigured = computed(() => flowStatus.value.configured);
const flowId = computed(() => flowStatus.value.flowId);
const isFlowEnabled = computed(() => flowStatus.value.enabled);
const isFlowPublished = computed(
  () => isFlowConfigured.value && isFlowEnabled.value
);

watch(isFlowPublished, published => emit('statusChange', published), {
  immediate: true,
});

// INBOX_MGMT.WHATSAPP_FLOWS.CSAT_FLOW.* / .NPS_FLOW.*
const i18nPrefix = computed(
  () => `INBOX_MGMT.WHATSAPP_FLOWS.${props.flowType.toUpperCase()}_FLOW`
);

const tf = key => t(`${i18nPrefix.value}.${key}`);

const fetchFlowStatus = async () => {
  try {
    isLoading.value = true;
    const response = await WhatsappFlowsAPI.getFlowStatus(
      props.inbox.id,
      props.flowType
    );
    flowStatus.value = {
      configured: response.data.configured,
      flowId: response.data.flow_id,
      enabled: response.data.enabled,
    };
  } catch {
    // Error fetching flow status - silently fail as this is not critical
  } finally {
    isLoading.value = false;
  }
};

const createFlow = async () => {
  try {
    isCreating.value = true;
    const response = await WhatsappFlowsAPI.createFlow(
      props.inbox.id,
      props.flowType
    );
    flowStatus.value = {
      configured: true,
      flowId: response.data.flow_id,
      enabled: true,
    };
    useAlert(tf('SUCCESS'));
  } catch (error) {
    useAlert(error.response?.data?.error || tf('ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const toggleFlow = async () => {
  const newEnabled = !isFlowEnabled.value;
  try {
    await WhatsappFlowsAPI.toggleFlow(
      props.inbox.id,
      newEnabled,
      props.flowType
    );
    flowStatus.value.enabled = newEnabled;
    useAlert(newEnabled ? tf('TOGGLE.ENABLED') : tf('TOGGLE.DISABLED'));
  } catch (error) {
    useAlert(error.response?.data?.error || tf('TOGGLE.ERROR'));
  }
};

const openDeleteDialog = () => {
  deleteDialogRef.value?.open();
};

const deleteFlow = async () => {
  try {
    isDeleting.value = true;
    await WhatsappFlowsAPI.deleteFlow(props.inbox.id, props.flowType);
    flowStatus.value = {
      configured: false,
      flowId: null,
      enabled: true,
    };
    deleteDialogRef.value?.close();
    useAlert(tf('DELETE.SUCCESS'));
  } catch (error) {
    useAlert(error.response?.data?.error || tf('DELETE.ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

onMounted(() => {
  fetchFlowStatus();
});
</script>

<template>
  <SectionLayout :title="tf('TITLE')" :description="tf('DESCRIPTION')">
    <div class="flex flex-col gap-4">
      <div v-if="isLoading" class="text-sm text-n-slate-11">
        {{ tf('LOADING') }}
      </div>

      <template v-else>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ tf('STATUS') }}:
            </span>
            <span
              v-if="isFlowConfigured && isFlowEnabled"
              class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
            >
              {{ tf('ACTIVE') }}
            </span>
            <span
              v-else-if="isFlowConfigured && !isFlowEnabled"
              class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
            >
              {{ tf('DISABLED') }}
            </span>
            <span
              v-else
              class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
            >
              {{ tf('NOT_CONFIGURED') }}
            </span>
          </div>
          <Switch
            v-if="isFlowConfigured"
            :model-value="isFlowEnabled"
            @change="toggleFlow"
          />
        </div>

        <div v-if="isFlowConfigured" class="text-sm text-n-slate-11">
          {{ tf('FLOW_ID') }}: {{ flowId }}
        </div>

        <div v-if="!isFlowConfigured">
          <NextButton
            :label="tf('CREATE_BUTTON')"
            :is-loading="isCreating"
            @click="createFlow"
          />
        </div>

        <div v-if="isFlowConfigured">
          <NextButton
            :label="tf('DELETE.BUTTON')"
            color="ruby"
            variant="faded"
            @click="openDeleteDialog"
          />
        </div>
      </template>
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="tf('DELETE.CONFIRM_TITLE')"
      :description="tf('DELETE.CONFIRM_DESCRIPTION')"
      :confirm-button-label="tf('DELETE.CONFIRM_BUTTON')"
      :is-loading="isDeleting"
      @confirm="deleteFlow"
    />
  </SectionLayout>
</template>
